import { Link, useParams, useSearchParams } from 'react-router-dom'
import { ChevronLeft } from 'lucide-react'
import { useVendorDirectory } from '@/hooks/useVendors'
import { useDebouncedValue } from '@/hooks/useDebouncedValue'
import { requestErrorMessage } from '@/helpers/apiClient'
import { inputToCents } from '@/lib/price'
import { PageContainer } from '@/components/ui/page-container'
import { PageHeader } from '@/components/ui/page-header'
import { Field } from '@/components/ui/field'
import { Input } from '@/components/ui/input'
import { Select } from '@/components/ui/select'
import { Button } from '@/components/ui/button'
import { ErrorState, LoadingState } from '@/components/ui/states'
import { DirectoryVendorCard } from '@/components/directory/DirectoryVendorCard'
import { DIRECTORY_SERVICES } from '@lib/constants/vendor-directory'
import { PRICE_CENTS_MAX } from '@lib/constants/vendor-profile'
import { VENDOR_SERVICE_LABELS } from '@lib/enums/vendor-service.enum'
import { VENDOR_PRICE_UNITS, VENDOR_PRICE_UNIT_LABELS } from '@lib/enums/vendor-price-unit.enum'
import type { VendorDirectoryQuery } from '@lib/input/vendor-directory.input'
import type { DirectoryVendorDto, VendorDirectoryDto } from '@lib/dto/vendor-directory.dto'
import { possessive } from '@lib/helpers/format'

/** The amount, in cents, when the "up to" box holds one the API takes; null otherwise. */
function maxCentsOf(params: URLSearchParams): number | null {
  const cents = inputToCents(params.get('max') ?? '')
  return cents !== null && cents > 0 && cents <= PRICE_CENTS_MAX ? cents : null
}

/**
 * What the address asks for, as the API takes it: `service`, `q`, and a price
 * filter (`unit`, `max` in dollars as typed) only with a category, a unit and
 * an amount. Anything else in the address is ignored rather than refused.
 */
export function directoryQueryOf(params: URLSearchParams): VendorDirectoryQuery {
  const service = DIRECTORY_SERVICES.find((s) => s === params.get('service'))
  const unit = VENDOR_PRICE_UNITS.find((u) => u === params.get('unit'))
  const maxPrice = maxCentsOf(params)
  const query: VendorDirectoryQuery = { q: (params.get('q') ?? '').trim().slice(0, 100) }
  if (service === undefined) return query
  return unit === undefined || maxPrice === null ? { ...query, service } : { ...query, service, unit, maxPrice }
}

function Results({ eventId, data, query, onClear }: { eventId: string; data: VendorDirectoryDto; query: VendorDirectoryQuery; onClear: () => void }) {
  const filtered = query.service !== undefined || query.q !== ''
  if (data.vendors.length === 0) {
    return filtered ? (
      <div className="space-y-3 py-8 text-center">
        <p className="m-0 text-sm text-ink-muted">None of {possessive(data.venueName)} vendors match.</p>
        <Button variant="outline" size="sm" onClick={onClear}>
          Clear filters
        </Button>
      </div>
    ) : (
      <p className="py-8 text-center text-sm text-ink-muted">{data.venueName} hasn&rsquo;t added any vendors yet.</p>
    )
  }
  const cards = (vendors: DirectoryVendorDto[]) => (
    <ul className="m-0 grid list-none gap-3 p-0 md:grid-cols-2">
      {vendors.map((vendor) => (
        <DirectoryVendorCard key={vendor.vendorId} vendor={vendor} eventId={eventId} venueName={data.venueName} />
      ))}
    </ul>
  )
  const onRequest = data.vendors.filter((v) => v.match === 'on_request')
  return (
    <div className="space-y-6">
      {cards(data.vendors.filter((v) => v.match !== 'on_request'))}
      {onRequest.length === 0 ? null : (
        <section className="space-y-3">
          <h2 className="m-0 text-sm font-medium text-ink">Price on request</h2>
          {cards(onRequest)}
        </section>
      )}
    </div>
  )
}

/**
 * The venue's vendors, for the couple and the planner of an event there
 * (docs/vendor-growth.spec.md, "Couples find vendors"): everyone the venue
 * works with, recommended first, by category and price. The filters live in
 * the address, so "See all" links and the back button keep them.
 */
export function VendorDirectoryPage() {
  const { eventId = '' } = useParams()
  const [params, setParams] = useSearchParams()
  // The boxes show what is typed at once; the directory is asked once typing settles.
  const settled = useDebouncedValue(params.toString(), 300)
  const query = directoryQueryOf(new URLSearchParams(settled))
  const directory = useVendorDirectory(eventId, query)
  const venueName = directory.data?.venueName
  const service = DIRECTORY_SERVICES.find((s) => s === params.get('service'))
  const chosenUnit = VENDOR_PRICE_UNITS.find((u) => u === params.get('unit'))
  // The category's units, once its own answer is in; a unit already chosen stays offered, so it can be changed back.
  const knownUnits = service === undefined || directory.isPlaceholderData ? null : (directory.data?.units ?? null)
  const units = VENDOR_PRICE_UNITS.filter((u) => knownUnits?.includes(u) === true || u === chosenUnit)
  const maxTyped = params.get('max') ?? ''
  // Nothing to filter by, and nothing set that would need clearing.
  const noPrices = knownUnits !== null && knownUnits.length === 0 && chosenUnit === undefined && maxTyped === ''

  /** Change what the address asks for; a blank value takes it out. */
  function set(changes: Record<string, string>): void {
    setParams(
      (current) => {
        const next = new URLSearchParams(current)
        for (const [key, value] of Object.entries(changes)) {
          if (value === '') next.delete(key)
          else next.set(key, value)
        }
        return next
      },
      { replace: true },
    )
  }

  function body() {
    if (directory.isPending) return <LoadingState label="Loading the venue’s vendors…" />
    if (directory.isError) return <ErrorState message={requestErrorMessage(directory.error)} onRetry={() => void directory.refetch()} />
    return <Results eventId={eventId} data={directory.data} query={query} onClear={() => setParams({}, { replace: true })} />
  }

  return (
    <PageContainer className="space-y-6">
      <Link to={`/events/${eventId}`} className="inline-flex items-center gap-1 text-sm text-ink-muted transition-colors hover:text-ink">
        <ChevronLeft className="h-4 w-4" /> Back to the event
      </Link>
      <PageHeader
        title={venueName === undefined ? 'Vendors' : `Vendors at ${venueName}`}
        meta={venueName === undefined ? undefined : `Everyone ${venueName} works with, recommended first.`}
      />
      <div className="flex flex-wrap items-end gap-3">
        <Field label="Category" htmlFor="directory-service" className="w-48">
          <Select
            id="directory-service"
            value={service ?? ''}
            // A price means something only within a category.
            onChange={(e) => set({ service: e.target.value, unit: '', max: '' })}
          >
            <option value="">All categories</option>
            {DIRECTORY_SERVICES.map((service) => (
              <option key={service} value={service}>
                {VENDOR_SERVICE_LABELS[service]}
              </option>
            ))}
          </Select>
        </Field>
        {service === undefined ? null : (
          <>
            <Field
              label="Up to"
              htmlFor="directory-max"
              className="w-28"
              error={maxTyped !== '' && maxCentsOf(params) === null ? 'Enter an amount in dollars' : undefined}
              hint={noPrices ? 'No prices shown in this category yet.' : undefined}
            >
              <Input
                id="directory-max"
                inputMode="decimal"
                placeholder="$"
                value={maxTyped}
                disabled={noPrices}
                onChange={(e) => set({ max: e.target.value })}
              />
            </Field>
            <Field label="Per" htmlFor="directory-unit" className="w-36">
              <Select id="directory-unit" value={chosenUnit ?? ''} disabled={noPrices} onChange={(e) => set({ unit: e.target.value })}>
                <option value="">Choose</option>
                {units.map((unit) => (
                  <option key={unit} value={unit}>
                    {VENDOR_PRICE_UNIT_LABELS[unit]}
                  </option>
                ))}
              </Select>
            </Field>
          </>
        )}
        <Field label="Search by name" htmlFor="directory-q" className="w-56">
          <Input id="directory-q" value={params.get('q') ?? ''} maxLength={100} onChange={(e) => set({ q: e.target.value })} />
        </Field>
      </div>
      {body()}
    </PageContainer>
  )
}
